import { type FC, useCallback, type MouseEvent } from 'react';

import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { type MicroCopy } from '@vakantiesnl/types';
import { type Url } from 'next/dist/shared/lib/router/router';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useCustomStyles } from './Pagination.style';

export type PaginationProps = {
	pageCount: number;
	currentPage: number;
	microCopies: MicroCopy;
	setValue: (page: number) => void;
	/** Boolean to apply specific logic for bots */
	isBot?: boolean;
};

const previousPage = (currentPage: number): number => (currentPage > 1 ? currentPage - 1 : currentPage);
const nextPage = (currentPage: number, pageCount: number): number =>
	currentPage < pageCount ? currentPage + 1 : currentPage;

export const PaginationComponent: FC<PaginationProps> = ({ pageCount, currentPage, microCopies, setValue, isBot }) => {
	const setValueCallback = useCallback(
		(newValue: number) => {
			return (e: MouseEvent<HTMLAnchorElement>): void => {
				e.preventDefault();
				setValue(newValue);
			};
		},
		[setValue],
	);

	const { classes } = useCustomStyles();

	const router = useRouter();
	const queryParams = new URLSearchParams(router.asPath.split('?')[1]);
	const basePath = router.asPath.split('?')[0];

	const totalPageNumbers = Array.from({ length: pageCount }, (_, i) => i + 1); // +1 because pages start at 1 instead of 0
	const pageNumbersToShow = totalPageNumbers.slice(
		currentPage > 2 ? currentPage - 3 : 0,
		currentPage > 2 ? currentPage + 2 : 5,
	);

	const showPrevButton = currentPage > 1;
	const showNextButton = currentPage < pageCount;

	return (
		<div className={classes.paginationContainer} data-cy="pagination">
			<div className={classes.prevButton}>
				{showPrevButton && (
					<Link
						shallow={true}
						href={getPaginationHref(basePath, queryParams, previousPage(currentPage))}
						onClick={setValueCallback(previousPage(currentPage))}
						data-cy="pagination-prev"
						className={classes.link}
					>
						<Button variant="tertiary" asText={true} leadingIcon="chevronLeft">
							{microCopies['common.previous']}
						</Button>
					</Link>
				)}
			</div>

			<div className={classes.pages} data-cy="pagination-pages">
				{pageNumbersToShow.map((pageNumber) => {
					const isCurrentPage = pageNumber === currentPage;

					return (
						<Link
							href={getPaginationHref(basePath, queryParams, pageNumber, isBot)}
							shallow={true}
							onClick={setValueCallback(pageNumber)}
							key={pageNumber}
							data-cy={isCurrentPage ? 'pagination-current-page' : `pagination-page-${pageNumber}`}
							className={classes.link}
						>
							<Button variant="tertiary" active={isCurrentPage} asText={true}>
								{pageNumber}
							</Button>
						</Link>
					);
				})}
			</div>

			<div className={classes.nextButton}>
				{showNextButton && (
					<Link
						shallow={true}
						href={getPaginationHref(basePath, queryParams, nextPage(currentPage, pageCount), isBot)}
						onClick={setValueCallback(nextPage(currentPage, pageCount))}
						data-cy="pagination-next"
						className={classes.link}
					>
						<Button variant="tertiary" asText={true} trailingIcon="chevronRight">
							{microCopies['common.next']}
						</Button>
					</Link>
				)}
			</div>
		</div>
	);
};

export function getPaginationHref(
	basePath: string,
	queryParams: URLSearchParams,
	nextPage: number,
	isBot?: boolean,
): Url {
	if (nextPage === 1) {
		queryParams.delete('page');
	} else {
		queryParams.set('page', nextPage.toString());
	}

	// Only apply the page query param for bots to avoid persisting query params
	if (isBot)
		return {
			pathname: basePath,
			query: queryParams.has('page') ? `page=${queryParams.get('page')}` : undefined,
		};

	return {
		pathname: basePath,
		query: queryParams.toString(),
	};
}
